/**
 * Onglet Structure spatiale — Hemispheres, quadrants, maisons
 * Reproduit pixel-perfect structure-spatiale.php
 */

import { useState } from 'react';
import { colors } from '../../styles/tokens.js';
import { PLANET_SYMBOLS } from './planetColors.js';
import type { ChartAnalysis } from '../../types/clients.js';

type StructureData = NonNullable<ChartAnalysis['structureSpatiale']>;

/** Descriptions des quadrants — identiques au PHP */
const DESC_QUADRANTS: Record<string, { nom: string; desc: string }> = {
  'Q1': { nom: 'Quadrant I (M.1-3)', desc: 'Developpement personnel, identite, ressources propres' },
  'Q2': { nom: 'Quadrant II (M.4-6)', desc: 'Racines, creativite, travail quotidien' },
  'Q3': { nom: 'Quadrant III (M.7-9)', desc: 'Relations, transformations, expansion' },
  'Q4': { nom: 'Quadrant IV (M.10-12)', desc: 'Carriere, collectif, spiritualite' },
};

interface Props {
  structure: StructureData;
}

export function StructureTab({ structure }: Props) {
  const [showHelp, setShowHelp] = useState(false);

  // Calculs hemispheres comme le PHP
  const estCount = structure.hemispheres.Est.count;
  const ouestCount = structure.hemispheres.Ouest.count;
  const nordCount = structure.hemispheres.Nord.count;
  const sudCount = structure.hemispheres.Sud.count;

  const totalEO = estCount + ouestCount;
  const pctEst = totalEO > 0 ? Math.round((estCount / totalEO) * 100) : 50;

  const totalNS = nordCount + sudCount;
  const pctNord = totalNS > 0 ? Math.round((nordCount / totalNS) * 100) : 50;

  // En cas d'egalite : Ouest et Sud priment (identique au PHP)
  const orientationEO = estCount > ouestCount ? 'Est (autonomie)' : 'Ouest (relations)';
  const orientationNS = nordCount > sudCount ? 'Nord (interiorite)' : 'Sud (extraversion)';

  // Trouver le quadrant dominant
  let quadrantDominant = 'Q1';
  let maxCount = 0;
  for (const [key, q] of Object.entries(structure.quadrants)) {
    if (q.count > maxCount) {
      maxCount = q.count;
      quadrantDominant = key;
    }
  }

  // Maisons tres actives (3+ planetes)
  const maisonsChargees: { num: number; count: number }[] = [];
  for (let m = 1; m <= 12; m++) {
    const pls = structure.maisons[m]?.planetes ?? [];
    if (pls.length >= 3) {
      maisonsChargees.push({ num: m, count: pls.length });
    }
  }

  return (
    <div style={{
      background: '#fff', borderRadius: 8, padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      {/* Section header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.25rem', paddingBottom: '0.75rem',
        borderBottom: `2px solid ${colors.border}`,
      }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: colors.text, fontWeight: 700 }}>
          <i className="bi bi-grid-3x3-gap-fill" style={{ color: colors.accent, marginRight: 8 }} />
          Structure Spatiale
        </h3>
        <button
          onClick={() => setShowHelp(!showHelp)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 12px', fontSize: '0.8rem', fontWeight: 500,
            background: showHelp ? colors.accent : '#f3f4f6',
            color: showHelp ? '#fff' : colors.textMid,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            transition: 'all 0.2s', fontFamily: 'inherit',
          }}
        >
          <i className="bi bi-question-circle" />
          Aide
        </button>
      </div>

      {/* Rubrique d'aide */}
      {showHelp && (
        <div style={{
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
          padding: '1.25rem', marginBottom: '1.25rem', fontSize: '0.85rem',
          lineHeight: 1.7, color: '#1e3a5f',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0c4a6e' }}>
              Comprendre la Structure Spatiale
            </h4>
            <button
              onClick={() => setShowHelp(false)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '1.2rem', color: '#64748b', lineHeight: 1,
              }}
            >
              &times;
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83E\uDDED'} Les Hemispheres
              </p>
              <p style={{ margin: '0 0 4px 0' }}>La repartition des planetes revele les <strong>zones de vie prioritaires</strong> :</p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li><strong>Est (M.10-3)</strong> : Focus sur soi, autonomie, independance</li>
                <li><strong>Ouest (M.4-9)</strong> : Focus sur les autres, relations, partenariats</li>
                <li><strong>Nord (M.1-6)</strong> : Vie interieure, intime, privee</li>
                <li><strong>Sud (M.7-12)</strong> : Vie sociale, publique, visible</li>
              </ul>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83D\uDCCA'} Les Quadrants
              </p>
              <p style={{ margin: '0 0 4px 0' }}>Chaque quadrant represente un <strong>domaine d'experience</strong> :</p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li><strong>Quadrant I (M.1-3)</strong> : Identite, corps, ressources personnelles</li>
                <li><strong>Quadrant II (M.4-6)</strong> : Racines, creativite, sante/travail</li>
                <li><strong>Quadrant III (M.7-9)</strong> : Relations, transformation, expansion</li>
                <li><strong>Quadrant IV (M.10-12)</strong> : Carriere, collectif, spiritualite</li>
              </ul>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83C\uDFE0'} Les Maisons
              </p>
              <p style={{ margin: 0 }}>Une maison contenant <strong>3+ planetes</strong> indique un secteur de vie tres actif, concentrant beaucoup d'energie et d'attention.</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83D\uDCA1'} Interpretation
              </p>
              <p style={{ margin: 0 }}>Un theme concentre dans un hemisphere ou quadrant indique des priorites de vie marquees. Un theme plus reparti suggere une approche plus equilibree des differents domaines.</p>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        {/* === Colonne gauche : Hemispheres === */}
        <div>
          <div style={cardAnalysis}>
            <h4 style={cardTitle}>
              {'\uD83E\uDDED'} Hemispheres
            </h4>

            {/* Axe Est-Ouest */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 6 }}>
                <span>Est (Soi)</span>
                <span>Ouest (Autres)</span>
              </div>
              <div style={{ display: 'flex', height: 24, borderRadius: 12, overflow: 'hidden', background: '#e5e7eb' }}>
                <div style={{
                  width: `${pctEst}%`, background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 600, fontSize: '0.85rem',
                }}>
                  {estCount}
                </div>
                <div style={{
                  width: `${100 - pctEst}%`, background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 600, fontSize: '0.85rem',
                }}>
                  {ouestCount}
                </div>
              </div>
            </div>

            {/* Axe Nord-Sud */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 6 }}>
                <span>Nord (Intime)</span>
                <span>Sud (Social)</span>
              </div>
              <div style={{ display: 'flex', height: 24, borderRadius: 12, overflow: 'hidden', background: '#e5e7eb' }}>
                <div style={{
                  width: `${pctNord}%`, background: 'linear-gradient(90deg, #22c55e, #4ade80)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 600, fontSize: '0.85rem',
                }}>
                  {nordCount}
                </div>
                <div style={{
                  width: `${100 - pctNord}%`, background: 'linear-gradient(90deg, #ef4444, #f87171)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 600, fontSize: '0.85rem',
                }}>
                  {sudCount}
                </div>
              </div>
            </div>
          </div>

          {/* Synthese orientation */}
          <div style={{
            marginTop: '1rem', padding: '1rem',
            background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
            borderRadius: 10,
          }}>
            <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: '0.5rem' }}>
              <i className="bi bi-compass" style={{ marginRight: 6 }} />
              Orientation dominante
            </div>
            <div style={{ fontSize: '0.95rem', color: '#1e3a8a' }}>
              {orientationEO} + {orientationNS}
            </div>
          </div>
        </div>

        {/* === Colonne droite : Quadrants === */}
        <div>
          <div style={cardAnalysis}>
            <h4 style={cardTitle}>
              {'\uD83D\uDCCA'} Repartition par Quadrants
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {Object.entries(structure.quadrants).map(([key, q]) => {
                const isDominant = key === quadrantDominant;
                const info = DESC_QUADRANTS[key];
                return (
                  <div key={key} style={{
                    padding: '1rem',
                    background: isDominant ? '#dbeafe' : '#f9fafb',
                    borderRadius: 10,
                    border: isDominant ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  }}>
                    <div style={{
                      fontWeight: isDominant ? 700 : 500, fontSize: '0.9rem',
                      marginBottom: '0.5rem',
                      color: isDominant ? '#1d4ed8' : colors.text,
                    }}>
                      {info?.nom ?? key} {isDominant ? '\u2605' : ''}
                    </div>
                    <div style={{
                      fontFamily: "'Noto Sans Symbols 2', sans-serif",
                      fontSize: '1.3rem', letterSpacing: 3, marginBottom: '0.25rem',
                    }}>
                      {q.planetes.length > 0
                        ? q.planetes.map((p) => PLANET_SYMBOLS[p] ?? p).join(' ')
                        : '\u2014'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: colors.textMid }}>
                      {q.count} planete{q.count > 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Distribution par maison */}
      <div style={{ ...cardAnalysis, marginTop: '1.5rem' }}>
        <h4 style={cardTitle}>
          {'\uD83C\uDFE0'} Distribution par Maison
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 6 }}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((num) => {
            const pls = structure.maisons[num]?.planetes ?? [];
            const hasContent = pls.length > 0;
            const count = pls.length;
            return (
              <div key={num} style={{
                padding: '0.5rem', textAlign: 'center',
                background: hasContent ? (count >= 3 ? '#fef3c7' : '#dbeafe') : '#f9fafb',
                borderRadius: 8,
                border: count >= 3 ? '2px solid #f59e0b' : '1px solid #e5e7eb',
              }}>
                <div style={{
                  fontWeight: 700, fontSize: '0.75rem',
                  color: hasContent ? (count >= 3 ? '#92400e' : '#1d4ed8') : '#9ca3af',
                }}>
                  M{num}
                </div>
                {hasContent ? (
                  <div style={{
                    fontFamily: "'Noto Sans Symbols 2', sans-serif",
                    fontSize: '0.9rem', marginTop: 2,
                  }}>
                    {pls.map((p) => PLANET_SYMBOLS[p] ?? p).join('')}
                  </div>
                ) : (
                  <div style={{ color: '#d1d5db', fontSize: '0.8rem' }}>{'\u2014'}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Maisons tres actives */}
        {maisonsChargees.length > 0 && (
          <div style={{
            marginTop: '1rem', padding: '0.75rem',
            background: '#fef3c7', borderRadius: 8,
            fontSize: '0.85rem', color: '#92400e',
          }}>
            <i className="bi bi-lightbulb" style={{ marginRight: 6 }} />
            <strong>Maisons tres actives : </strong>
            {maisonsChargees.map((m, i) => (
              <span key={m.num}>
                Maison {m.num} ({m.count} planetes){i < maisonsChargees.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Signes / Maisons interceptes */}
      {structure.interceptes && structure.interceptes.signesInterceptes.length > 0 && (
        <div style={{ ...cardAnalysis, marginTop: '1.5rem' }}>
          <h4 style={cardTitle}>
            <i className="bi bi-lock" style={{ color: '#7c3aed' }} />
            Signes et Maisons interceptes
          </h4>

          {/* Signes interceptes */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: colors.text, marginBottom: '0.5rem' }}>
              Signes interceptes
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
              {structure.interceptes.signesInterceptes.map((si) => (
                <div key={si.signe} style={{
                  padding: '0.75rem', borderRadius: 8,
                  background: '#f5f3ff', border: '1px solid #ddd6fe',
                }}>
                  <div style={{ fontWeight: 600, color: '#5b21b6', fontSize: '0.9rem' }}>
                    {si.signe}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#7c3aed', marginTop: 2 }}>
                    dans Maison {si.maison}
                  </div>
                  {si.planetes.length > 0 && (
                    <div style={{
                      marginTop: 4, fontSize: '0.8rem', color: '#6d28d9',
                      fontFamily: "'Noto Sans Symbols 2', sans-serif",
                    }}>
                      {si.planetes.map((p) => PLANET_SYMBOLS[p] ?? p).join(' ')}
                      <span style={{ fontFamily: 'inherit', marginLeft: 4, fontStyle: 'italic' }}>
                        (intercepte{si.planetes.length > 1 ? 's' : ''})
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Maisons liees */}
          {structure.interceptes.maisonsLiees.length > 0 && (
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: colors.text, marginBottom: '0.5rem' }}>
                Maisons liees
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {structure.interceptes.maisonsLiees.map((ml) => (
                  <div key={ml.signe} style={{
                    padding: '0.5rem 0.75rem', borderRadius: 8,
                    background: '#faf5ff', border: '1px solid #e9d5ff',
                    fontSize: '0.85rem', color: '#7c3aed',
                  }}>
                    <strong>{ml.signe}</strong> sur M{ml.maisons.join(' et M')}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Note explicative */}
          <div style={{
            marginTop: '0.75rem', padding: '0.5rem 0.75rem',
            background: '#faf5ff', borderRadius: 6,
            fontSize: '0.8rem', color: '#6b21a8', fontStyle: 'italic',
          }}>
            Un signe intercepte est entierement contenu dans une maison sans en toucher la cuspide.
            Les planetes dans un signe intercepte expriment leur energie de maniere plus interieure et progressive.
          </div>
        </div>
      )}
    </div>
  );
}

const cardAnalysis: React.CSSProperties = {
  background: colors.bgPage,
  borderRadius: 8,
  padding: '1.25rem',
  marginBottom: '1rem',
};

const cardTitle: React.CSSProperties = {
  margin: '0 0 1rem 0',
  fontSize: '0.95rem',
  color: colors.text,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};
